import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  createNotification,
  listNotifications,
  sendNotification,
  deleteNotification,
} from "../../api/notifications";
import axios from "../../api/httpClient";
import { getApiBaseUrl } from "../../data/authStorage";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";

const NOTIFICATIONS_CACHE_KEY = "cehr_notifications_page_v1";

const NotificationManagement = () => {
  const cachedData = useMemo(
    () =>
      readSessionCache(NOTIFICATIONS_CACHE_KEY, {
        notifications: [],
        currentPage: 1,
        totalPages: 1,
      }),
    [],
  );
  const [notifications, setNotifications] = useState(
    cachedData?.notifications || [],
  );
  const [loading, setLoading] = useState(!cachedData?.notifications?.length);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sendingNotificationId, setSendingNotificationId] = useState("");
  const [pendingDeleteNotification, setPendingDeleteNotification] = useState(null);
  const [deletingNotification, setDeletingNotification] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(cachedData?.currentPage || 1);
  const [totalPages, setTotalPages] = useState(cachedData?.totalPages || 1);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [levelOptions, setLevelOptions] = useState([]);
  const [isLoadingRecipientOptions, setIsLoadingRecipientOptions] =
    useState(false);
  const apiBaseUrl = getApiBaseUrl();

  // Form state for new notification
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    actionUrl: "",
    actionLabel: "",
    channel: "general",
    recipients: {
      sendToAll: false,
      departmentIds: [],
      levelIds: [],
      employeeIds: [],
    },
    scheduledFor: null,
  });

  const [recipientType, setRecipientType] = useState("all"); // all, departments, levels, employees

  const loadNotifications = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent && notifications.length === 0) {
        setLoading(true);
      }

      try {
        const response = await listNotifications({
          page: currentPage,
          limit: 10,
        });
        const nextNotifications = response.notifications || [];
        const nextTotalPages = response.pagination?.pages || 1;

        setNotifications(nextNotifications);
        setTotalPages(nextTotalPages);
        writeSessionCache(NOTIFICATIONS_CACHE_KEY, {
          notifications: nextNotifications,
          currentPage,
          totalPages: nextTotalPages,
        });
      } catch (error) {
        toast.error(error.message || "Failed to load notifications");
      } finally {
        setLoading(false);
      }
    },
    [currentPage, notifications.length],
  );

  useEffect(() => {
    loadNotifications({ silent: true });
  }, [currentPage, loadNotifications]);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadNotifications({ silent: true });
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [loadNotifications]);

  useEffect(() => {
    const loadRecipientOptions = async () => {
      if (!apiBaseUrl) {
        return;
      }

      try {
        setIsLoadingRecipientOptions(true);
        const [departmentsResponse, levelsResponse] = await Promise.all([
          axios.get(`${apiBaseUrl}/organizations/departments`),
          axios.get(`${apiBaseUrl}/organizations/levels`),
        ]);

        setDepartmentOptions(departmentsResponse?.data?.departments || []);
        setLevelOptions(levelsResponse?.data?.levels || []);
      } catch (error) {
        toast.error("Unable to load recipient options right now");
      } finally {
        setIsLoadingRecipientOptions(false);
      }
    };

    loadRecipientOptions();
  }, [apiBaseUrl]);

  const handleFormChange = (e) => {
    const { name, value, type: inputType, checked } = e.target;

    if (inputType === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleRecipientTypeChange = (nextRecipientType) => {
    setRecipientType(nextRecipientType);
    setFormData((prev) => ({
      ...prev,
      recipients: {
        ...prev.recipients,
        departmentIds:
          nextRecipientType === "departments"
            ? prev.recipients.departmentIds
            : [],
        levelIds:
          nextRecipientType === "levels" ? prev.recipients.levelIds : [],
      },
    }));
  };

  const toggleRecipientSelection = (group, id) => {
    setFormData((prev) => {
      const currentItems = prev.recipients[group] || [];
      const alreadySelected = currentItems.includes(id);

      return {
        ...prev,
        recipients: {
          ...prev.recipients,
          [group]: alreadySelected
            ? currentItems.filter((itemId) => itemId !== id)
            : [...currentItems, id],
        },
      };
    });
  };

  const getChannelMapping = (channel) => {
    switch (channel) {
      case "email":
        return { email: true, inApp: false };
      case "in-app":
        return { email: false, inApp: true };
      case "broadcast":
      case "general":
      default:
        return { email: true, inApp: true };
    }
  };

  const handleCreateNotification = async () => {
    if (!formData.title.trim() || !formData.message.trim()) {
      toast.error("Title and message are required");
      return;
    }

    if (
      recipientType === "departments" &&
      formData.recipients.departmentIds.length === 0
    ) {
      toast.error("Select at least one department");
      return;
    }

    if (
      recipientType === "levels" &&
      formData.recipients.levelIds.length === 0
    ) {
      toast.error("Select at least one level");
      return;
    }

    const recipients = {
      sendToAll: recipientType === "all",
      departmentIds:
        recipientType === "departments"
          ? formData.recipients.departmentIds
          : [],
      levelIds: recipientType === "levels" ? formData.recipients.levelIds : [],
      employeeIds:
        recipientType === "employees" ? formData.recipients.employeeIds : [],
    };

    try {
      setIsSubmitting(true);
      const payload = {
        title: formData.title,
        message: formData.message,
        actionUrl: formData.actionUrl.trim() || undefined,
        actionLabel: formData.actionLabel.trim() || undefined,
        type: formData.channel,
        recipients,
        channels: getChannelMapping(formData.channel),
        scheduledFor: formData.scheduledFor,
      };

      await createNotification(payload);
      toast.success("Notification created successfully");
      setShowModal(false);
      resetForm();
      loadNotifications();
      setIsSubmitting(false);
    } catch (error) {
      toast.error(error.message || "Failed to create notification");
      setIsSubmitting(false);
    }
  };

  const handleSendNotification = async (notificationId) => {
    try {
      setSendingNotificationId(notificationId);
      await sendNotification(notificationId, "Sent");
      toast.success("Notification sent successfully");
      loadNotifications();
    } catch (error) {
      toast.error(error.message || "Failed to send notification");
    } finally {
      setSendingNotificationId("");
    }
  };

  const handleDeleteNotification = async (notificationId) => {
      try {
        setDeletingNotification(true);
        await deleteNotification(notificationId);
        toast.success("Notification deleted successfully");
        loadNotifications();
      } catch (error) {
        toast.error(error.message || "Failed to delete notification");
      } finally { setDeletingNotification(false); }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      message: "",
      actionUrl: "",
      actionLabel: "",
      channel: "general",
      recipients: {
        sendToAll: false,
        departmentIds: [],
        levelIds: [],
        employeeIds: [],
      },
      scheduledFor: null,
    });
    setRecipientType("all");
  };

  const getStatusColor = (status) => {
    const colors = {
      Draft: "bg-gray-100 text-gray-800",
      Scheduled: "bg-blue-100 text-blue-800",
      Sent: "bg-green-100 text-green-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const modalContent = (
    <div className="max-h-96 overflow-y-auto">
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Title *</label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleFormChange}
          maxLength={200}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Notification title"
        />
        <p className="text-xs text-gray-500">{formData.title.length}/200</p>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Message *</label>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleFormChange}
          maxLength={2000}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Notification message"
        />
        <p className="text-xs text-gray-500">{formData.message.length}/2000</p>
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium mb-1">Action URL (optional)</label>
          <input type="text" name="actionUrl" value={formData.actionUrl} onChange={handleFormChange} placeholder="/productivity/project-details?project=..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <p className="mt-1 text-xs text-gray-500">Use an internal path beginning with /.</p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Action label (optional)</label>
          <input type="text" name="actionLabel" value={formData.actionLabel} onChange={handleFormChange} maxLength={80} placeholder="Open project" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Channel</label>
        <select
          name="channel"
          value={formData.channel}
          onChange={handleFormChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="general">General</option>
          <option value="email">Email</option>
          <option value="in-app">In-App</option>
          <option value="broadcast">Broadcast</option>
        </select>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Send To</label>
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="radio"
              checked={recipientType === "all"}
              onChange={() => handleRecipientTypeChange("all")}
              className="mr-2"
            />
            <span>All Staff</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              checked={recipientType === "departments"}
              onChange={() => handleRecipientTypeChange("departments")}
              className="mr-2"
            />
            <span>Specific Department(s)</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              checked={recipientType === "levels"}
              onChange={() => handleRecipientTypeChange("levels")}
              className="mr-2"
            />
            <span>Specific Level(s)</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              checked={recipientType === "employees"}
              onChange={() => handleRecipientTypeChange("employees")}
              className="mr-2"
            />
            <span>Selected Employee(s)</span>
          </label>
        </div>
      </div>

      {recipientType === "departments" ? (
        <div className="mb-4 rounded-lg border border-gray-200 p-3">
          <p className="mb-2 text-sm font-medium text-gray-900">
            Select Department(s)
          </p>

          {isLoadingRecipientOptions ? (
            <p className="text-sm text-gray-500">Loading departments...</p>
          ) : departmentOptions.length === 0 ? (
            <p className="text-sm text-gray-500">No departments available.</p>
          ) : (
            <div className="max-h-36 overflow-y-auto space-y-2">
              {departmentOptions.map((department) => (
                <label
                  key={department.id}
                  className="flex items-center gap-2 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={formData.recipients.departmentIds.includes(
                      department.id,
                    )}
                    onChange={() =>
                      toggleRecipientSelection("departmentIds", department.id)
                    }
                  />
                  <span>
                    {department.departmentName ||
                      department.name ||
                      "Unnamed department"}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
      ) : null}

      {recipientType === "levels" ? (
        <div className="mb-4 rounded-lg border border-gray-200 p-3">
          <p className="mb-2 text-sm font-medium text-gray-900">
            Select Level(s)
          </p>

          {isLoadingRecipientOptions ? (
            <p className="text-sm text-gray-500">Loading levels...</p>
          ) : levelOptions.length === 0 ? (
            <p className="text-sm text-gray-500">No levels available.</p>
          ) : (
            <div className="max-h-36 overflow-y-auto space-y-2">
              {levelOptions.map((level) => (
                <label
                  key={level.id}
                  className="flex items-center gap-2 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={formData.recipients.levelIds.includes(level.id)}
                    onChange={() =>
                      toggleRecipientSelection("levelIds", level.id)
                    }
                  />
                  <span>
                    {level.levelName || level.name || "Unnamed level"}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
      ) : null}

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">
          Schedule For (Optional)
        </label>
        <input
          type="datetime-local"
          name="scheduledFor"
          value={formData.scheduledFor || ""}
          onChange={handleFormChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      {pendingDeleteNotification ? <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/55 p-4" onMouseDown={() => !deletingNotification && setPendingDeleteNotification(null)}><div role="dialog" aria-modal="true" className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onMouseDown={(event) => event.stopPropagation()}><h2 className="text-lg font-bold text-slate-900">Delete notification?</h2><p className="mt-2 text-sm leading-6 text-slate-500">This will permanently delete “{pendingDeleteNotification.title}” from the notification manager.</p><div className="mt-6 flex justify-end gap-3"><button type="button" disabled={deletingNotification} onClick={() => setPendingDeleteNotification(null)} className="rounded-xl border px-4 py-2.5 text-sm font-semibold text-slate-700">Cancel</button><button type="button" disabled={deletingNotification} onClick={async () => { await handleDeleteNotification(pendingDeleteNotification.id); setPendingDeleteNotification(null); }} className="rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white">{deletingNotification ? "Deleting..." : "Delete notification"}</button></div></div></div> : null}
      {selectedNotification ? <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/55 p-4" onMouseDown={() => setSelectedNotification(null)}><div role="dialog" aria-modal="true" className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl" onMouseDown={(event) => event.stopPropagation()}><div className="flex items-start justify-between gap-4"><div><p className="text-xs font-semibold uppercase tracking-wider text-blue-600">Notification details</p><h2 className="mt-1 text-xl font-bold text-slate-900">{selectedNotification.title}</h2></div><button type="button" onClick={() => setSelectedNotification(null)} className="text-2xl text-slate-400">×</button></div><p className="mt-5 whitespace-pre-wrap rounded-xl bg-slate-50 p-4 text-sm leading-6 text-slate-700">{selectedNotification.message || "No message content available."}</p><div className="mt-5 grid grid-cols-2 gap-3 text-sm"><div><p className="text-xs text-slate-400">Status</p><p className="font-semibold">{selectedNotification.status}</p></div><div><p className="text-xs text-slate-400">Channel</p><p className="font-semibold">{selectedNotification.type}</p></div><div><p className="text-xs text-slate-400">Recipients</p><p className="font-semibold">{selectedNotification.readCount ? `${selectedNotification.readCount} received` : "Pending"}</p></div><div><p className="text-xs text-slate-400">Created</p><p className="font-semibold">{new Date(selectedNotification.createdAt).toLocaleString()}</p></div></div><div className="mt-6 flex justify-end"><button type="button" onClick={() => setSelectedNotification(null)} className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white">Close</button></div></div></div> : null}
    </div>
  );

  return (
    <div className="p-6 font-Nunito min-h-screen">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold">Notification Manager</h1>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
        >
          + New Notification
        </button>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-lg">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Create Notification
              </h2>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="text-2xl text-gray-500 hover:text-gray-800"
              >
                &times;
              </button>
            </div>

            {modalContent}

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateNotification}
                disabled={isSubmitting}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? "Creating..." : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <FetchLoadingState message="Loading notifications..." className="min-h-[360px] rounded-xl bg-white" />
      ) : notifications.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-12 text-center">
          <p className="text-gray-600 mb-4">No notifications yet</p>
          <button
            onClick={() => setShowModal(true)}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Create your first notification →
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="divide-y md:hidden">{notifications.map((notif) => <article key={notif.id} className="space-y-3 p-4"><div className="flex items-start justify-between gap-3"><div className="min-w-0"><h3 className="truncate font-semibold text-gray-900">{notif.title}</h3><p className="mt-1 text-xs text-gray-500">{notif.type} · {new Date(notif.createdAt).toLocaleDateString()}</p></div><span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${getStatusColor(notif.status)}`}>{notif.status}</span></div><p className="text-sm text-gray-600">{notif.readCount ? `${notif.readCount} received` : "Pending recipients"}</p><div className="flex gap-4 text-sm">{notif.status === "Draft" ? <button onClick={() => handleSendNotification(notif.id)} disabled={sendingNotificationId === notif.id} className="font-medium text-blue-600 disabled:opacity-50">{sendingNotificationId === notif.id ? "Sending..." : "Send"}</button> : null}<button onClick={() => setPendingDeleteNotification(notif)} className="font-medium text-red-600">Delete</button></div></article>)}</div>
          <table className="hidden w-full md:table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                  Recipients
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                  Channel
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {notifications.map((notif) => (
                <tr
                  key={notif.id}
                  onClick={() => setSelectedNotification(notif)}
                  className="cursor-pointer hover:bg-gray-50"
                >
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {notif.title}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(notif.status)}`}
                    >
                      {notif.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {notif.readCount
                      ? `${notif.readCount} received`
                      : "Pending"}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {notif.type}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {new Date(notif.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-sm flex gap-2">
                    {notif.status === "Draft" && (
                      <button
                        onClick={() => handleSendNotification(notif.id)}
                        disabled={sendingNotificationId === notif.id}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {sendingNotificationId === notif.id
                          ? "Sending..."
                          : "Send"}
                      </button>
                    )}
                    <button
                      onClick={(event) => { event.stopPropagation(); setPendingDeleteNotification(notif); }}
                      className="text-red-600 hover:text-red-800 font-medium"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="px-6 py-4 border-t flex justify-center gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50"
              >
                ←
              </button>
              <span className="px-3 py-1">
                {currentPage} of {totalPages}
              </span>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
                className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50"
              >
                →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationManagement;
